<template>
  <div>
    <div class="calendar-header-redesigned border rounded">
      <div class="d-flex justify-content-center align-items-center p-2 gap-2 flex-wrap">
        <b-button size="sm" variant="outline-secondary" class="nav-btn" @click="goToPrev" :title="$t('Previous Month')">
          <feather-icon icon="ChevronLeftIcon" />
        </b-button>

        <div
            @click="openDatePicker"
            class="mx-1 calendar-title-clickable text-uppercase d-flex align-items-center gap-2 p-50  rounded border hover-shadow transition"
            :title="$t('Click to choose month and year')"
            style="cursor: pointer; background-color: #f8f9fa;"
        >
          <span class="fw-bold fs-5 text-dark">{{ currentTitle }}</span>
        </div>

        <b-button size="sm" variant="outline-secondary" class="nav-btn" @click="goToNext" :title="$t('Next Month')">
          <feather-icon icon="ChevronRightIcon" />
        </b-button>
      </div>

      <div class="filter-section p-2 border-top">
        <b-row class="g-2 align-items-center">
          <b-col cols="12" md="5">
            <vue-autosuggest
                v-model="searchQueryDriverFilter"
                :suggestions="filteredOptionsDriverFilter"
                :input-props="inputPropsDriverFilter"
                @selected="selectHandlerDriverFilter"
                @input="onInputChangeDriverFilter"
            >
              <template slot-scope="{suggestion}">
                <span class="my-suggestion-item">{{ suggestion.item.name }}</span>
              </template>
            </vue-autosuggest>
          </b-col>
          <b-col cols="12" md="5">
            <vue-autosuggest
                v-model="searchQueryAssetFilter"
                :suggestions="filteredOptionsAssetFilter"
                :input-props="inputPropsAssetFilter"
                @selected="selectHandlerAssetFilter"
                @input="onInputChangeAssetFilter"
            >
              <template slot-scope="{suggestion}">
                <span class="my-suggestion-item">{{ suggestion.item.nickname || suggestion.item.plate || suggestion.item.chassisNumber }}</span>
              </template>
            </vue-autosuggest>
          </b-col>
          <b-col cols="12" md="2">
            <b-button variant="outline-secondary" @click="limparFiltros" class="w-100">
              <i class="fas fa-eraser me-1"></i>
              {{ $t('Clear') }}
            </b-button>
          </b-col>
        </b-row>
      </div>
    </div>

    <b-alert
        :show="showEmptyStateMessage"
        variant="info"
        class="mt-2"
    >
      <div class="d-flex align-items-center p-50">
        <i class="fas fa-info-circle me-2"></i>
        <p class="mb-0 small">
          {{ $t('Select driver or asset to view calendar') }}
        </p>
      </div>
    </b-alert>

    <full-calendar :key="calendarKey" ref="refCalendar" :options="calendarOptions" class="full-calendar text-capitalize"/>

    <b-modal id="modal-date-picker" v-model="showDatePickerModal" :title="$t('Choose Month and Year')" hide-footer centered
             size="md" header-class="border-0 pb-0" body-class="pt-2">
      <template #modal-header="{ close }">
        <div class="w-100 d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <div>
              <h4 class="modal-title mb-0 text-dark">{{ $t('Navigate Calendar') }}</h4>
              <p class="text-muted mb-0 small">{{ $t('Select desired month and year') }}</p>
            </div>
          </div>
          <b-button variant="link" @click="close" class="text-muted p-1">
            <i class="fas fa-times fa-lg"></i>
          </b-button>
        </div>
      </template>

      <div class="date-picker-content">
        <b-row class="g-3 mb-4">
          <b-col cols="12" md="6">
            <div class="form-group-enhanced">
              <label class="form-label-enhanced">
                <i class="fas fa-calendar me-2 text-primary"></i>{{ $t('Month') }}
              </label>
              <v-select v-model="selectedMonth" :options="monthOptions" label="label" :reduce="option => option.value"
                        :placeholder="$t('Select month')" class="month-select-enhanced" :clearable="false" />
            </div>
          </b-col>
          <b-col cols="12" md="6">
            <div class="form-group-enhanced">
              <label class="form-label-enhanced">
                <i class="fas fa-calendar-year me-2 text-primary"></i>{{ $t('Year') }}
              </label>
              <v-select v-model="selectedYear" :options="yearOptions" :placeholder="$t('Select year')"
                        class="year-select-enhanced" :clearable="false" />
            </div>
          </b-col>
        </b-row>
        <div class="preview-section mb-4 p-3 bg-light rounded">
          <div class="d-flex align-items-center justify-content-center">
            <i class="fas fa-eye me-2 text-muted"></i>
            <span class="text-muted small">{{ $t('Preview') }}</span>
            <span class="ms-2 fw-bold text-dark" v-if="selectedMonth && selectedYear">
              {{ getMonthName(selectedMonth) }} {{ selectedYear }}
            </span>
            <span class="ms-2 text-muted" v-else>{{ $t('Select month and year') }}</span>
          </div>
        </div>
        <div class="modal-actions d-flex justify-content-between align-items-center">
          <b-button variant="outline-primary" @click="goToToday" class="btn-action-secondary">
            <i class="fas fa-calendar-day me-2"></i>{{ $t('Go to Today') }}
          </b-button>
          <div class="d-flex gap-2">
            <b-button variant="outline-secondary" @click="closeDatePicker" class="btn-action-cancel mr-1">
              <i class="fas fa-times me-2"></i>{{ $t('Cancel') }}
            </b-button>
            <b-button variant="primary" @click="navigateToSelectedDate" :disabled="!selectedMonth || !selectedYear"
                      class="btn-action-primary">
              <i class="fas fa-arrow-right me-2"></i>{{ $t('Navigate') }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal id="modal-driver" v-model="showDriverModal" :title="$t('Driver Management')"  centered
             size="lg" header-class="border-0 pb-0" body-class="pt-2">
      <b-row>
        <b-col md="6">
          <b-form-group :label="$t('Driver') + '*'" label-for="driver">
            <vue-autosuggest id="driver" v-model="searchQueryDriverModal" :suggestions="filteredOptionsDriver"
                             :input-props="inputProps" @selected="selectHandlerDriver" @input="onInputChangeDriver">
              <template slot-scope="{suggestion}">
                <span class="my-suggestion-item">{{ suggestion.item.name }}</span>
              </template>
            </vue-autosuggest>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group :label="$t('Asset') + '*'" label-for="asset">
            <vue-autosuggest id="driver" v-model="searchQueryAssetModal" :suggestions="filteredOptionsAsset"
                             :input-props="inputProps" @selected="selectHandlerAsset" @input="onInputChangeAsset">
              <template slot-scope="{suggestion}">
                <span class="my-suggestion-item">{{ suggestion.item.nickname || suggestion.item.plate || suggestion.item.chassisNumber }}</span>
              </template>
            </vue-autosuggest>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6">
          <b-form-group :label="$t('Start Date') + '*'" label-for="datetime-picker">
            <flat-pickr
                id="datetime-picker"
                v-model="driverManagementData.start"
                :config="flatpickrConfig"
                class="form-control"
                :placeholder="$t('Select date and time')"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group :label="$t('End Date')" label-for="end-datetime-picker">
            <flat-pickr
                id="end-datetime-picker"
                v-model="driverManagementData.end"
                :config="endPickerConfig" class="form-control"
                :placeholder="$t('Select the end date and time (optional)')"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="12">
          <b-form-group :label="$t('Note')" label-for="note">
            <b-form-textarea
                id="note"
                v-model="driverManagementData.note"
                :placeholder="$t('Type something')"
                rows="3"
                max-rows="6"
            ></b-form-textarea>
          </b-form-group>
        </b-col>
      </b-row>
      <template #modal-footer>
        <div>
          <b-button variant="secondary" @click="showDriverModal = false" class="mr-1">
            {{ $t('Cancel') }}
          </b-button>
          <b-button variant="primary" @click="handleSave">
            {{ $t('Save') }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BFormInput, BFormGroup, BForm, BRow, BCol, BButton, BCardText, BFormTextarea, BNavItemDropdown, BDropdownItem, BImg,
  BAvatar, BBadge, BInputGroupAppend, BInputGroup, BModal, BSpinner, BTable, BTab, BTabs, BCard, BAlert, BLink,
  BDropdown, BCollapse, VBToggle, VBTooltip, BCardBody, BCardHeader, BPagination,
} from 'bootstrap-vue'
import store from '@/store'
import { onUnmounted } from '@vue/composition-api'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css';
import { VueAutosuggest } from 'vue-autosuggest'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import driverStoreModule from "@/views/main/driver/driverStoreModule";
import assetStoreModole from "@/views/main/asset/assetStoreModule";

export default {
  components:{
    ToastificationContent, FullCalendar, BFormInput, BFormGroup, BForm, BRow, BCol, BButton, BCardText, BFormTextarea,
    BNavItemDropdown, BDropdownItem, BImg, BAvatar, BBadge, BInputGroupAppend, BInputGroup, BModal, BSpinner, BTable,
    BTab, BTabs, BCard, BAlert, BLink, BDropdown, BCollapse, VBToggle, VBTooltip, BCardBody, BCardHeader, BPagination,
    'v-select': vSelect, VueAutosuggest, flatPickr
  },
  data() {
    return {
      driverFilter: null,
      assetFilter: null,
      searchQueryDriverFilter: '',
      filteredOptionsDriverFilter:[],
      searchQueryAssetFilter: '',
      filteredOptionsAssetFilter:[],
      searchQueryDriverModal: '',
      filteredOptionsDriver:[],
      searchQueryAssetModal:'',
      filteredOptionsAsset:[],
      calendarKey: 1,
      calendarOptions: {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        weekends: true,
        editable: false,
        droppable: false,
        eventResizableFromStart: true,
        headerToolbar: { left: '', center: '', right: '' },
        events: [],
        dateClick: this.handleDateClick,
        eventClick: this.handleEventClick,
        locale: 'pt-br',
        eventContent: this.renderEventContent,
        eventDisplay: 'block',
      },
      currentTitle: '',
      selectedMonth: null,
      selectedYear: null,
      showDatePickerModal: false,
      showDriverModal: false,
      yearOptions: [],
      driverManagementData: {
        id: null, driverId: null, assetId: null, start: null, end: null, note: null,
      },
      flatpickrConfig: {
        locale: 'pt-br', enableTime: true, dateFormat: 'Y-m-d\\TH:i:S', time_24hr: true, altInput: true, altFormat: 'd/m/Y H:i',
      },
      endPickerConfig: {
        locale: 'pt-br', enableTime: true, dateFormat: 'Y-m-d\\TH:i:S', time_24hr: true, altInput: true, altFormat: 'd/m/Y H:i', minDate: null, maxDate: null,
      },
    }
  },

  watch: {
    driverFilter() {
      this.fetchDriverAttributions();
    },
    assetFilter() {
      this.fetchDriverAttributions();
    },
    'driverManagementData.start'(newStartDate) {
      if (newStartDate) {
        const startDate = new Date(newStartDate);
        this.endPickerConfig.minDate = startDate;
        const maxEndDate = new Date(startDate);
        maxEndDate.setHours(maxEndDate.getHours() + 23);
        maxEndDate.setMinutes(maxEndDate.getMinutes() + 59);
        this.endPickerConfig.maxDate = maxEndDate;
        if (this.driverManagementData.end && new Date(this.driverManagementData.end) < startDate) {
          this.driverManagementData.end = null;
        }
      } else {
        this.endPickerConfig.minDate = null;
        this.endPickerConfig.maxDate = null;
        this.driverManagementData.end = null;
      }
    },
  },

  computed: {
    showEmptyStateMessage() {
      return !this.driverFilter && !this.assetFilter;
    },
    monthOptions() {
      return [
        { label: this.$t('January'), value: '01' }, { label: this.$t('February'), value: '02' }, { label: this.$t('March'), value: '03' },
        { label: this.$t('April'), value: '04' }, { label: this.$t('May'), value: '05' }, { label: this.$t('June'), value: '06' },
        { label: this.$t('July'), value: '07' }, { label: this.$t('August'), value: '08' }, { label: this.$t('September'), value: '09' },
        { label: this.$t('October'), value: '10' }, { label: this.$t('November'), value: '11' }, { label: this.$t('December'), value: '12' },
      ]
    },
    inputPropsDriverFilter() {
      return {
        id: 'autosuggest__input-filter-driver',
        class: 'form-control',
        placeholder: this.$t('Filter by Driver'),
      }
    },
    inputPropsAssetFilter() {
      return {
        id: 'autosuggest__input-filter-asset',
        class: 'form-control',
        placeholder: this.$t('Filter by Asset'),
      }
    },
    inputProps() {
      return {
        id: 'autosuggest__input-modal-driver',
        class: 'form-control',
        placeholder: this.$t('Search for driver'),
      }
    },
  },

  mounted() {
    this.calendarApi = this.$refs.refCalendar.getApi()
    this.initializeYearOptions()
    this.updateTitle()
    this.getVisibleRange()
  },

  methods: {
    limparFiltros() {
      this.driverFilter = null;
      this.assetFilter = null;
      this.searchQueryDriverFilter = '';
      this.searchQueryAssetFilter = '';
      this.filteredOptionsDriverFilter = [];
      this.filteredOptionsAssetFilter = [];
    },
    selectHandlerDriverFilter(option) {
      this.driverFilter = option.item;
      this.searchQueryDriverFilter = option.item.name;
      this.filteredOptionsDriverFilter = [];
    },
    onInputChangeDriverFilter(text) {
      if (text === '' || text === undefined) {
        this.driverFilter = null;
        this.filteredOptionsDriverFilter = [];
        return;
      }
      if (text.length < 3) return;
      store.dispatch(`${this.DRIVER_APP_STORE_MODULE_NAME}/fetchDrivers`, { q: text, page: 1, perPage: 10 })
          .then(response => { this.filteredOptionsDriverFilter = [{ data: response.data.data }]; })
          .catch(() => {});
    },
    selectHandlerAssetFilter(option) {
      this.assetFilter = option.item;
      this.searchQueryAssetFilter = option.item.nickname || option.item.plate || option.item.chassisNumber;
      this.filteredOptionsAssetFilter = [];
    },
    onInputChangeAssetFilter(text) {
      if (text === '' || text === undefined) {
        this.assetFilter = null;
        this.filteredOptionsAssetFilter = [];
        return;
      }
      if (text.length < 3) return;
      store.dispatch(`${this.ASSET_APP_STORE_MODULE_NAME}/fetchAssets`, { q: text, page: 1, perPage: 10 })
          .then(response => { this.filteredOptionsAssetFilter = [{ data: response.data.data }]; })
          .catch(() => {});
    },

    async fetchDriverAttributions() {
      if (!this.driverFilter && !this.assetFilter) {
        this.calendarOptions.events = [];
        return;
      }
      try {
        const start = this.visibleStartDate.toISOString().split('T')[0]
        const end = this.visibleEndDate.toISOString().split('T')[0]
        const query = {
          start, end,
          driverId: this.driverFilter ? this.driverFilter.id : null,
          assetId: this.assetFilter ? this.assetFilter.id : null,
          page: 1, perPage: 500
        }
        const response = await store.dispatch(`${this.DRIVER_APP_STORE_MODULE_NAME}/searchDriverAttributions`, query)
        this.calendarOptions.events = response.data.map(item => ({
          id: item.id,
          title: `${item.driverName} - ${item.assetNickname || item.assetPlate || item.assetChassis}`,
          start: item.start,
          end: item.end,
          extendedProps: item
        }))
      } catch (err) {
        this.calendarOptions.events = [];
      }
    },

    renderEventContent(arg) {
      const attrib = arg.event.extendedProps;
      const start = arg.event.start;
      const end = arg.event.end;

      const formatCompactDateTime = (date) => {
        if (!date) return '';
        const d = new Date(date);
        if (isNaN(d.getTime())) return '';
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${day}/${month} ${hours}:${minutes}`;
      };

      const formattedStart = formatCompactDateTime(start);
      let timeHtml = `<span>🗓️ ${formattedStart}</span>`;
      let durationHtml = '';

      if (end) {
        const formattedEnd = formatCompactDateTime(end);
        timeHtml += `<span class="mx-1">→</span><span>🏁 ${formattedEnd}</span>`;

        const diffMs = end.getTime() - start.getTime();

        if (diffMs > 0) {
          const totalMinutes = Math.floor(diffMs / (1000 * 60));
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          const formattedMinutes = String(minutes).padStart(2, '0');
          const durationText = `${hours}:${formattedMinutes}`;
          durationHtml = `<span class="duration-badge">⏳ ${durationText}</span>`;
        }
      }

      return {
        html: `
            <div class="fc-event-card-melhorado fc-event-card-compacto">
                <div class="info-line">
                    <strong>👷‍♂️ ${attrib.driverName}</strong>
                    <span class="mx-1 text-muted">|</span>
                    <span>🚜 ${attrib.assetNickname || attrib.assetPlate || attrib.assetChassis}</span>
                </div>
                <div class="time-line">
                    <div class="date-range">${timeHtml}</div>
                    ${durationHtml}
                </div>
            </div>`
      };
    },

    validateForm() {
      if (!this.driverManagementData.driverId || !this.driverManagementData.assetId || !this.driverManagementData.start) {
        this.showMsg(this.$t('The fields with (*) are required'));
        return false;
      }
      if (this.driverManagementData.end) {
        const startDate = new Date(this.driverManagementData.start);
        const endDate = new Date(this.driverManagementData.end);
        if (endDate < startDate) {
          this.showMsg(this.$t('End date cannot be before start date'));
          return false;
        }
        const diff = endDate.getTime() - startDate.getTime();
        const maxDiff = (23 * 60 * 60 * 1000) + (59 * 60 * 1000);
        if (diff > maxDiff) {
          this.showMsg(this.$t('Attribution duration cannot exceed 23h59m'));
          return false;
        }
      }
      return true;
    },

    handleSave() {
      if (!this.validateForm()) {
        return;
      }
      if (this.driverManagementData.id) {
        const payload = {
          Id: this.driverManagementData.id,
          End: this.driverManagementData.end,
          Notes: this.driverManagementData.note,
        };
        store.dispatch(`${this.DRIVER_APP_STORE_MODULE_NAME}/updateDriverAttribution`, payload)
            .then(() => {
              this.showMsg(this.$t('Update successful'));
              this.showDriverModal = false;
              this.fetchDriverAttributions();
            })
            .catch(() => this.showMsg(this.$t('Failed to update')));
      } else {
        const payload = {
          DriverId: this.driverManagementData.driverId,
          AssetId: this.driverManagementData.assetId,
          StartDate: this.driverManagementData.start.toString(),
          End: this.driverManagementData.end,
          Note: this.driverManagementData.note,
        };
        store.dispatch(`${this.DRIVER_APP_STORE_MODULE_NAME}/addDriverAttribution`, { driverData: payload })
            .then(() => {
              this.showMsg(this.$t('Successful registration'));
              this.showDriverModal = false;
              this.fetchDriverAttributions();
            })
            .catch(() => this.showMsg(this.$t('Failed to save')));
      }
    },

    showMsg(title){
      this.$toast({
        component: ToastificationContent,
        props: { title: title, icon: 'InfoIcon' },
      })
    },

    selectHandlerDriver(option) {
      this.filteredOptionsDriver = [];
      this.searchQueryDriverModal = option.item.name;
      this.driverManagementData.driverId = option.item.id;
    },
    onInputChangeDriver(text) {
      if (text === '' || text === undefined || text.length < 3) {
        this.driverManagementData.driverId = null;
        return;
      }
      store.dispatch(`${this.DRIVER_APP_STORE_MODULE_NAME}/fetchDrivers`, { q: text, page: 1, perPage: 5 })
          .then(response => { this.filteredOptionsDriver = [{ data: response.data.data }]; })
          .catch(() => {});
    },
    selectHandlerAsset(option) {
      this.filteredOptionsAsset = [];
      this.searchQueryAssetModal = option.item.nickname || option.item.plate || option.item.chassisNumber;
      this.driverManagementData.assetId = option.item.id;
    },
    onInputChangeAsset(text) {
      if (text === '' || text === undefined || text.length < 3) {
        this.driverManagementData.assetId = null;
        return;
      }
      store.dispatch(`${this.ASSET_APP_STORE_MODULE_NAME}/fetchAssets`, { q: text, page: 1, perPage: 5 })
          .then(response => { this.filteredOptionsAsset = [{ data: response.data.data }]; })
          .catch(() => {});
    },

    resetDriverManagementData() {
      this.driverManagementData = {
        id: null, driverId: null, assetId: null, start: null, end: null, note: null,
      };
      this.searchQueryDriverModal = '';
      this.searchQueryAssetModal = '';
      this.filteredOptionsDriver = [];
      this.filteredOptionsAsset = [];
      this.endPickerConfig.minDate = null;
      this.endPickerConfig.maxDate = null;
    },

    handleEventClick(info) {
      const attributionId = info.event.id;
      store.dispatch(`${this.DRIVER_APP_STORE_MODULE_NAME}/getDriverAttribution`, attributionId)
          .then(response => {
            const data = response.data;
            this.driverManagementData = {
              id: data.id, driverId: data.driverId, assetId: data.assetId,
              start: data.start, end: data.end, note: data.note,
            };
            this.searchQueryDriverModal = data.driverName;
            this.searchQueryAssetModal = data.assetNickname || data.assetPlate || data.assetChassis;
            this.showDriverModal = true;
          })
          .catch(error => {
            this.showMsg(this.$t('Could not load attribution data'));
          });
    },

    initializeYearOptions() {
      const currentYear = new Date().getFullYear();
      this.yearOptions = [];
      for (let year = currentYear - 5; year <= currentYear + 5; year++) {
        this.yearOptions.push(year);
      }
    },
    navigateToSelectedDate() {
      if (this.selectedMonth && this.selectedYear) {
        const calendarApi = this.$refs.refCalendar.getApi();
        const targetDate = new Date(this.selectedYear, parseInt(this.selectedMonth) - 1, 1);
        calendarApi.gotoDate(targetDate);
        this.$nextTick(() => { this.updateTitle(); this.getVisibleRange(); });
        this.closeDatePicker();
      }
    },
    goToToday() {
      const calendarApi = this.$refs.refCalendar.getApi();
      calendarApi.gotoDate(new Date());
      this.$nextTick(() => { this.updateTitle(); this.getVisibleRange(); });
      this.closeDatePicker();
    },
    getMonthName(monthValue) {
      const monthMap = {
        '01': this.$t('January'), '02': this.$t('February'), '03': this.$t('March'), '04': this.$t('April'),
        '05': this.$t('May'), '06': this.$t('June'), '07': this.$t('July'), '08': this.$t('August'),
        '09': this.$t('September'), '10': this.$t('October'), '11': this.$t('November'), '12': this.$t('December'),
      };
      return monthMap[monthValue] || '';
    },
    handleDateClick(info){
      // 1. Limpa os dados de atribuições anteriores para garantir um formulário novo.
      this.resetDriverManagementData();

      // 2. Verifica se um motorista foi selecionado no filtro principal.
      if (this.driverFilter) {
        // Se sim, preenche o ID e o nome do motorista no formulário do modal.
        this.driverManagementData.driverId = this.driverFilter.id;
        this.searchQueryDriverModal = this.driverFilter.name;
      }

      // 3. Verifica se um ativo foi selecionado no filtro principal.
      if (this.assetFilter) {
        // Se sim, preenche o ID e o nome/placa/chassi do ativo no formulário.
        this.driverManagementData.assetId = this.assetFilter.id;
        this.searchQueryAssetModal = this.assetFilter.nickname || this.assetFilter.plate || this.assetFilter.chassisNumber;
      }

      // 4. Define a data de início com base no dia clicado no calendário.
      this.driverManagementData.start = info.date;

      // 5. Exibe o modal para o usuário preencher o restante das informações.
      this.showDriverModal = true;
    },
    getVisibleRange() {
      const calendarApi = this.$refs.refCalendar.getApi();
      const view = calendarApi.view;
      this.visibleStartDate = new Date(view.activeStart);
      this.visibleEndDate = new Date(view.activeEnd);
      this.visibleEndDate.setDate(this.visibleEndDate.getDate() - 1);
      this.fetchDriverAttributions();
    },
    goToPrev() {
      const calendarApi = this.$refs.refCalendar.getApi();
      calendarApi.prev();
      this.$nextTick(() => { this.updateTitle(); this.getVisibleRange(); });
    },
    goToNext() {
      const calendarApi = this.$refs.refCalendar.getApi();
      calendarApi.next();
      this.$nextTick(() => { this.updateTitle(); this.getVisibleRange(); });
    },
    updateTitle() {
      this.currentTitle = this.$refs.refCalendar.getApi().view.title;
    },
    openDatePicker() {
      this.initializeCurrentDate();
      this.showDatePickerModal = true;
    },
    initializeCurrentDate() {
      const calendarApi = this.$refs.refCalendar.getApi();
      const currentDate = calendarApi.getDate();
      this.selectedMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
      this.selectedYear = currentDate.getFullYear();
    },
    closeDatePicker() {
      this.showDatePickerModal = false;
    },
  },
  setup() {
    const DRIVER_APP_STORE_MODULE_NAME = 'app-driver'
    const ASSET_APP_STORE_MODULE_NAME = 'app-asset'
    if (!store.hasModule(DRIVER_APP_STORE_MODULE_NAME)) store.registerModule(DRIVER_APP_STORE_MODULE_NAME, driverStoreModule)
    if (!store.hasModule(ASSET_APP_STORE_MODULE_NAME)) store.registerModule(ASSET_APP_STORE_MODULE_NAME, assetStoreModole)
    onUnmounted(() => {
      if (store.hasModule(ASSET_APP_STORE_MODULE_NAME)) store.unregisterModule(ASSET_APP_STORE_MODULE_NAME)
      if (store.hasModule(DRIVER_APP_STORE_MODULE_NAME)) store.unregisterModule(DRIVER_APP_STORE_MODULE_NAME)
    })
    return {
      DRIVER_APP_STORE_MODULE_NAME,
      ASSET_APP_STORE_MODULE_NAME,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.calendar-header-redesigned {
  background-color: #ffffff;
}

.filter-section {
  background-color: #f8f8f8;
}

.calendar-title-clickable {
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s ease-in-out;
  display: inline-flex;
  align-items: center;
  color: #5e5873;

  &:hover {
    background-color: #f8f8f8;
  }
}

.fc-daygrid-event {
  background-color: transparent !important;
  border: none !important;
  cursor: pointer;
}

.fc-event-card-melhorado {
  background-color: #eef1ff;
  color: #5e5873;
  padding: 8px;
  border-radius: 6px;
  font-size: 0.8rem;
  border-left: 4px solid #7367f0;
  transition: all 0.2s ease-in-out;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    background-color: #e6e9ff;
  }
}

.fc-event-card-compacto {
  padding: 6px 8px;
  font-size: 0.75rem;
}

.fc-event-card-compacto .info-line,
.fc-event-card-compacto .time-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 5px;
}

.fc-event-card-compacto .date-range {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0 3px;
}

.fc-event-card-compacto .time-line {
  justify-content: flex-start;
  font-size: 0.7rem;
  color: #5e5873;
}

.duration-badge {
  background-color: rgba(115, 103, 240, 0.1);
  color: #7367f0;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  white-space: nowrap;
}

.nav-btn {
  width: 34px;
  height: 34px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  border-radius: 0.375rem !important; // Assegura a sobreposição do estilo do b-button
}

.today-btn {
  font-size: 0.85rem;
  font-weight: 500;
  height: 34px;
  background-color: #6e6b7b !important;
  border-color: #6e6b7b !important;

  &:hover {
    background-color: #5e5873 !important;
  }
}

.calendar-title-clickable:hover {
  background-color: #e9ecef;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  transition: all 0.2s ease-in-out;
}
</style>